/**
 * @fileoverview Checkbox Component
 * 
 * Accessible checkbox input using Radix UI primitives.
 * Provides checked/unchecked states with visual feedback.
 * 
 * @module components/Checkbox
 */

import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from 'lucide-react';

import { cn } from '../lib/utils';

/**
 * Checkbox component for boolean input selections
 * 
 * Built on Radix UI Checkbox primitive for accessibility and keyboard support.
 * Displays a checkmark icon when checked.
 * 
 * @component
 * @param {Object} props - Component props extending Radix Checkbox.Root props
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Rendered checkbox element
 * 
 * @example
 * ```tsx
 * // Basic usage
 * <Checkbox id="terms" />
 * 
 * // Controlled checkbox
 * const [checked, setChecked] = useState(false);
 * <Checkbox 
 *   checked={checked} 
 *   onCheckedChange={setChecked}
 * />
 * 
 * // With label
 * <div className="flex items-center space-x-2">
 *   <Checkbox id="terms" />
 *   <label htmlFor="terms">Accept terms and conditions</label>
 * </div>
 * 
 * // Disabled state
 * <Checkbox disabled checked={true} />
 * ```
 * 
 * @remarks
 * Features:
 * - Keyboard navigation support (Space to toggle)
 * - Focus ring for accessibility
 * - Disabled state styling
 * - Customizable via className
 * - Uses Radix UI for robust accessibility
 */
const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      'peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn('flex items-center justify-center text-current')}
    >
      <Check className='h-4 w-4' />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
